// path: frontend/src/pages/AnimatedBg.tsx
import * as React from "react";


function isSignedMediaUrl(url: string): boolean {
  return (
    url.includes("X-Goog-Algorithm=") ||
    url.includes("X-Goog-Signature=") ||
    url.includes("X-Amz-Algorithm=") ||
    url.includes("X-Amz-Signature=")
  );
}

function withCacheBust(url: string): string {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (isSignedMediaUrl(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}cb=${Date.now()}`;
}
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "@/lib/api-config";
import { auth } from "@/lib/firebase";

type StyleKey =
    | "neon_waves"
    | "particles_soft"
    | "gradient_pan"
    // Medical presets
    | "med_pathology"
    | "med_biochemistry"
    | "med_physiology"
    | "med_microbiology"
    | "med_pharmacology"
    | "med_biostatistics"
    | "med_public_health"
    // Unified default
    | "med_unified_blue";

const STYLE_LABELS: Record<StyleKey | string, string> = {
    neon_waves: "Neon Waves",
    particles_soft: "Particles (soft)",
    gradient_pan: "Gradient Pan",
    med_pathology: "Pathology",
    med_biochemistry: "Biochemistry",
    med_physiology: "Physiology",
    med_microbiology: "Microbiology",
    med_pharmacology: "Pharmacology",
    med_biostatistics: "Biostatistics",
    med_public_health: "Public Health",
    med_unified_blue: "Unified Blue",
};

// 🔒 User quota interface
interface UserQuota {
    user_id: string;
    plan: "free" | "starter" | "pro";
    active: boolean;
    videos: {
        used: number;
        limit: number;
        remaining: number;
    };
    longform_credits: number;
    features: {
        formats: string[];
        watermark: string;
        backgrounds: string;
        render_speed: string;
        hd_export: boolean;
        commercial_license: boolean;
        can_edit: boolean;
    };
}

// Env
const MODE = (import.meta as any)?.env?.MODE as string | undefined;
const IS_PROD = MODE === "production";

// 🔒 Helper to get auth headers using Firebase SDK
async function getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    };

    if (!auth) {
        return headers;
    }

    const user = auth.currentUser;
    if (!user) {
        return headers;
    }

    try {
        const token = await user.getIdToken();
        headers.Authorization = `Bearer ${token}`;
    } catch (e) {
        console.error("Failed to get auth token:", e);
    }

    return headers;
}

// 🔒 Get plan from quota system
function getLegacyPlan(): "free" | "pro" | "enterprise" {
    const wPlan = (window as any).__PLAN__;
    if (wPlan === "pro" || wPlan === "enterprise") return wPlan;
    const ls = localStorage.getItem("plan");
    if (ls === "pro" || ls === "enterprise") return ls as any;
    return "free";
}

// Unified, strict-blue parameters
const UNIFIED = {
    HUE: 210,
    HUE_MIN: 205,
    HUE_MAX: 215,
    SAT: 1.3,
    BRI: 0.12,
} as const;

const DEFAULTS = {
    style: "med_unified_blue" as StyleKey,
    video_format: "9:16" as "9:16" | "16:9" | "1:1",
    duration: 6,
    fps: 30,
    style_hue: UNIFIED.HUE,
    style_sat: UNIFIED.SAT,
    style_bri: UNIFIED.BRI,
} as const;

const clamp = (v: number, lo: number, hi: number) =>
    Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : lo;

const cb = (url: string) =>
    withCacheBust(url);

async function extractError(r: Response): Promise<string> {
    const ct = r.headers.get("content-type") || "";
    try {
        if (ct.includes("application/json")) {
            const j: any = await r.json();
            const d = j?.detail;
            if (typeof d === "string") return d;
            if (d && typeof d === "object") return JSON.stringify(d, null, 2);
            if (typeof j?.error === "string") return j.error;
            return JSON.stringify(j, null, 2);
        }
        const txt = await r.text();
        return txt || `${r.status} ${r.statusText}`;
    } catch {
        return `${r.status} ${r.statusText}`;
    }
}

export default function AnimatedBg() {
    const nav = useNavigate();

    // ✅ ADD: Loading state to prevent render issues
    const [isInitialized, setIsInitialized] = React.useState(false);
    const [quota, setQuota] = React.useState<UserQuota | null>(null);

    const legacyPlan = React.useMemo(() => getLegacyPlan(), []);
    const plan = quota?.plan || (legacyPlan === "enterprise" ? "pro" : legacyPlan);

    const [style, setStyle] = React.useState<StyleKey>(DEFAULTS.style);
    const [videoFormat, setVideoFormat] = React.useState<"9:16" | "16:9" | "1:1">(
        DEFAULTS.video_format,
    );
    const [duration, setDuration] = React.useState<number>(DEFAULTS.duration);
    const [fps, setFps] = React.useState<number>(DEFAULTS.fps);

    const [hue, setHue] = React.useState<number>(DEFAULTS.style_hue);
    const [sat, setSat] = React.useState<number>(DEFAULTS.style_sat);
    const [bri, setBri] = React.useState<number>(DEFAULTS.style_bri);

    const [generating, setGenerating] = React.useState(false);
    const [url, setUrl] = React.useState<string | null>(null);
    const [cleanUrl, setCleanUrl] = React.useState<string | null>(null);
    const [err, setErr] = React.useState<string | null>(null);

    const [thumbLoading, setThumbLoading] = React.useState(false);
    const [thumbUrl, setThumbUrl] = React.useState<string | null>(null);

    const [available, setAvailable] = React.useState<string[]>([
        "med_unified_blue",
        "neon_waves",
        "particles_soft",
        "gradient_pan",
        "med_pathology",
        "med_biochemistry",
        "med_physiology",
        "med_microbiology",
        "med_pharmacology",
        "med_biostatistics",
        "med_public_health",
    ]);

    // ✅ IMPROVED: Better error handling for quota loading
    const loadQuota = React.useCallback(async () => {
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE_URL}/api/payment/checkQuota`, {
                headers,
            });

            if (response.ok) {
                const data = await response.json();
                setQuota(data);
                console.log("✅ Quota loaded:", data);
            } else if (response.status === 401) {
                console.warn("⚠️ Quota check unauthorized (401). Continuing without quota.");
                setQuota(null); // Just continue without quota, don't crash
            } else {
                console.warn(`⚠️ Quota check failed (${response.status}). Continuing without quota.`);
                setQuota(null); // Continue without quota
            }
        } catch (error) {
            console.error("❌ Failed to load quota:", error);
            setQuota(null); // Don't crash - just continue without quota info
        } finally {
            // ✅ Mark as initialized even if quota fails
            setIsInitialized(true);
        }
    }, []);

    React.useEffect(() => {
        loadQuota();
    }, [loadQuota]);

    React.useEffect(() => {
        document.title = "Animated Background Builder";
    }, []);

    // ✅ IMPROVED: Better error handling for template loading
    React.useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const headers = await getAuthHeaders();
                const r = await fetch(
                    `${API_BASE_URL}/api/video/background/templates`,
                    { headers },
                );

                // ✅ Don't throw on 401, just use defaults
                if (r.status === 401) {
                    console.warn("⚠️ Templates endpoint requires auth. Using defaults.");
                    if (!alive) return;
                    setAvailable(available);
                    return;
                }

                const j = await r.json().catch(() => ({}));
                const templates: string[] = Array.isArray(j?.templates)
                    ? j.templates.map((t: any) =>
                        typeof t === 'string' ? t : (t.style || t.id || t.name)
                    ).filter((s: any) => typeof s === 'string')
                    : [];
                if (!alive) return;
                const merged = Array.from(new Set([...available, ...templates]));

                setAvailable(merged);
            } catch (error) {
                console.error("❌ Failed to load templates:", error);
                if (!alive) return;
                // ✅ Use defaults on error
                setAvailable(available);
            }
        })();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan]);

    React.useEffect(() => {
        try {
            const u = new URL(window.location.href);
            const s = (u.searchParams.get("style") as StyleKey) || null;
            const f =
                (u.searchParams.get("fmt") as "9:16" | "16:9" | "1:1") || null;
            const d = u.searchParams.get("duration");
            const p = u.searchParams.get("fps");
            if (s) setStyle(s);
            if (f) setVideoFormat(f);
            if (d && !Number.isNaN(+d)) setDuration(clamp(+d, 1, 60));
            if (p && !Number.isNaN(+p)) setFps(clamp(+p, 10, 60));
        } catch {
            /* ignore */
        }
    }, []);

    const isUnified = style === "med_unified_blue";
    const controlsLocked = false;

    const appliedHue = isUnified
        ? clamp(UNIFIED.HUE, UNIFIED.HUE_MIN, UNIFIED.HUE_MAX)
        : clamp(hue, 0, 360);
    const appliedSat = isUnified ? UNIFIED.SAT : clamp(sat, 0.5, 2);
    const appliedBri = isUnified ? UNIFIED.BRI : clamp(bri, -0.3, 0.3);

    const payload = React.useMemo(
        () => ({
            style,
            video_format: videoFormat,
            duration: clamp(duration | 0, 1, 60),
            fps: clamp(fps | 0, 10, 60),
            style_hue: Number(appliedHue),
            style_sat: Number(appliedSat),
            style_bri: Number(appliedBri),
        }),
        [style, videoFormat, duration, fps, appliedHue, appliedSat, appliedBri],
    );

    const mp4Abort = React.useRef<AbortController | null>(null);
    const pngAbort = React.useRef<AbortController | null>(null);

    async function handleGenerate() {
        mp4Abort.current?.abort();
        mp4Abort.current = new AbortController();
        setGenerating(true);
        setErr(null);
        setUrl(null);
        setCleanUrl(null);

        try {
            const headers = await getAuthHeaders();
            const r = await fetch(
                `${API_BASE_URL}/api/video/background/animated`,
                {
                    method: "POST",
                    headers,
                    body: JSON.stringify(payload),
                    signal: mp4Abort.current.signal,
                },
            );
            if (!r.ok) throw new Error(await extractError(r));
            const j = await r.json();
            if (!j.background_url) throw new Error("No background_url returned");

            const clean = j.background_url;
            const busted = cb(j.background_url);

            setCleanUrl(clean);
            setUrl(busted);

            try {
                localStorage.setItem("selected_bg", clean);
                window.dispatchEvent(
                    new StorageEvent("storage", {
                        key: "selected_bg",
                        newValue: clean,
                    }),
                );
                console.log("✅ Background saved:", clean);
            } catch (e) {
                console.error("Failed to save background:", e);
            }
        } catch (e: any) {
            if (e?.name !== "AbortError") {
                setErr(e?.message || "Failed to generate background");
                console.error("Generation error:", e);
            }
        } finally {
            setGenerating(false);
        }
    }

    async function handlePreviewThumb() {
        pngAbort.current?.abort();
        pngAbort.current = new AbortController();
        setThumbLoading(true);
        setThumbUrl(null);
        setErr(null);

        try {
            const headers = await getAuthHeaders();
            const r = await fetch(
                `${API_BASE_URL}/api/video/background/thumbnail`,
                {
                    method: "POST",
                    headers,
                    body: JSON.stringify(payload),
                    signal: pngAbort.current.signal,
                },
            );
            if (!r.ok) throw new Error(await extractError(r));
            const j = await r.json();

            const thumbSrc = j.image_url || j.thumbnail_url || j.video_url;
            if (!thumbSrc) {
                console.error("Thumbnail response:", j);
                throw new Error("No thumbnail returned from server");
            }

            console.log("✅ Thumbnail received:", thumbSrc);
            setThumbUrl(cb(thumbSrc));
        } catch (e: any) {
            if (e?.name !== "AbortError") {
                const errorMsg = e?.message || "Failed to generate thumbnail";
                setErr(errorMsg);
                console.error("Thumbnail error:", e);
            }
        } finally {
            setThumbLoading(false);
        }
    }

    function handleUse() {
        let bgUrl: string;

        if (cleanUrl) {
            bgUrl = cleanUrl;
        } else if (url) {
            const parts = url.split("?");
            bgUrl = parts[0] || url;
        } else {
            alert("No background URL available");
            return;
        }

        try {
            const parts = bgUrl.split("?");
            const clean = parts[0] || bgUrl;

            localStorage.setItem("selected_bg", clean);
            window.dispatchEvent(
                new StorageEvent("storage", {
                    key: "selected_bg",
                    newValue: clean,
                }),
            );

            console.log("✅ Background set for quiz:", clean);
            nav("/");
        } catch (e) {
            console.error("Failed to set background:", e);
            alert("Failed to set background. Please try again.");
        }
    }

    function copyUrl() {
        let urlToCopy: string;

        if (cleanUrl) {
            urlToCopy = cleanUrl;
        } else if (url) {
            const parts = url.split("?");
            urlToCopy = parts[0] || url;
        } else {
            alert("No URL to copy");
            return;
        }

        navigator.clipboard
            ?.writeText(urlToCopy)
            .then(() => {
                alert("Copied URL to clipboard");
                console.log("Copied:", urlToCopy);
            })
            .catch(() => alert("Could not copy URL"));
    }

    React.useEffect(() => {
        setErr(null);
    }, [style, videoFormat, duration, fps, hue, sat, bri]);

    function getPlanName(p?: string): string {
        if (!p) return "Free";
        return p.charAt(0).toUpperCase() + p.slice(1);
    }

    const canUseAnimated =
        quota?.features.backgrounds === "all" || plan === "pro";

    // ✅ Show loading state while initializing
    if (!isInitialized) {
        return (
            <div className="p-6 max-w-3xl mx-auto flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-3xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Animated Background Builder</h1>
                <button
                    onClick={() => nav(-1)}
                    className="text-sm px-3 py-1.5 rounded border hover:bg-black/5 dark:hover:bg-white/10"
                    title="Go back"
                >
                    ← Back
                </button>
            </div>

            <p className="text-gray-600 dark:text-gray-300">
                {canUseAnimated
                    ? "Create custom animated backgrounds with full style control."
                    : "Unified default background with full style customization."}
            </p>

            <div className="grid sm:grid-cols-2 gap-4 bg-white dark:bg-gray-800 p-4 rounded-md">
                <label className="space-y-1">
                    <span className="text-sm font-medium">
                        Style
                    </span>
                    <select
                        value={style}
                        onChange={(e) => setStyle(e.target.value as StyleKey)}
                        className="w-full border p-2 rounded"
                        disabled={false}
                    >
                        {available.map((k) => (
                            <option key={k} value={k}>
                                {STYLE_LABELS[k] || k}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="space-y-1">
                    <span className="text-sm font-medium">Format</span>
                    <select
                        value={videoFormat}
                        onChange={(e) => setVideoFormat(e.target.value as any)}
                        className="w-full border p-2 rounded"
                    >
                        <option value="9:16">9:16 (Reels/TikTok)</option>
                        <option value="16:9">16:9 (YouTube)</option>
                        <option value="1:1">1:1 (Square)</option>
                    </select>
                </label>

                <label className="space-y-1">
                    <span className="text-sm font-medium">Duration (sec)</span>
                    <input
                        type="number"
                        min={2}
                        max={60}
                        value={duration}
                        onChange={(e) =>
                            setDuration(clamp(Number(e.target.value), 1, 60))
                        }
                        className="w-full border p-2 rounded"
                    />
                </label>

                <label className="space-y-1">
                    <span className="text-sm font-medium">FPS</span>
                    <input
                        type="number"
                        min={10}
                        max={60}
                        value={fps}
                        onChange={(e) =>
                            setFps(clamp(Number(e.target.value), 10, 60))
                        }
                        className="w-full border p-2 rounded"
                    />
                </label>

                <label className="space-y-1">
                    <span className="text-sm font-medium">
                        Hue (°) {controlsLocked && "🔒"}
                    </span>
                    <input
                        type="number"
                        min={0}
                        max={360}
                        value={isUnified ? appliedHue : hue}
                        onChange={(e) => setHue(clamp(Number(e.target.value), 0, 360))}
                        className="w-full border p-2 rounded"
                        disabled={controlsLocked}
                    />
                    {controlsLocked && (
                        <div className="text-xs text-slate-500">
                            Locked for unified style
                        </div>
                    )}
                </label>

                <label className="space-y-1">
                    <span className="text-sm font-medium">
                        Saturation {controlsLocked && "🔒"}
                    </span>
                    <input
                        step="0.05"
                        type="number"
                        min={0.5}
                        max={2.0}
                        value={isUnified ? appliedSat : sat}
                        onChange={(e) =>
                            setSat(clamp(Number(e.target.value), 0.5, 2))
                        }
                        className="w-full border p-2 rounded"
                        disabled={controlsLocked}
                    />
                    {controlsLocked && (
                        <div className="text-xs text-slate-500">
                            Locked for unified style
                        </div>
                    )}
                </label>

                <label className="space-y-1">
                    <span className="text-sm font-medium">
                        Brightness {controlsLocked && "🔒"}
                    </span>
                    <input
                        step="0.02"
                        type="number"
                        min={-0.3}
                        max={0.3}
                        value={isUnified ? appliedBri : bri}
                        onChange={(e) =>
                            setBri(clamp(Number(e.target.value), -0.3, 0.3))
                        }
                        className="w-full border p-2 rounded"
                        disabled={controlsLocked}
                    />
                    {controlsLocked && (
                        <div className="text-xs text-slate-500">
                            Locked for unified style
                        </div>
                    )}
                </label>
            </div>

            <div className="flex flex-wrap gap-3">
                <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50"
                >
                    {generating ? "Generating…" : "Generate MP4"}
                </button>
                <button
                    onClick={handlePreviewThumb}
                    disabled={thumbLoading}
                    className="border px-4 py-2 rounded disabled:opacity-50"
                    title="Quick image preview"
                >
                    {thumbLoading ? "Previewing…" : "Preview thumbnail"}
                </button>
                <button
                    onClick={() => nav("/backgrounds")}
                    className="border px-4 py-2 rounded"
                >
                    Back to Backgrounds
                </button>
            </div>

            {err && (
                <pre className="text-red-600 text-sm whitespace-pre-wrap break-words">
                    {err}
                </pre>
            )}

            {thumbUrl && (
                <div className="space-y-3">
                    <div className="text-sm text-gray-600 dark:text-gray-300 break-all">
                        Thumbnail URL: <code>{thumbUrl}</code>
                    </div>
                    <img
                        src={thumbUrl}
                        alt="Background preview"
                        className="w-full rounded border"
                        onError={(e) => {
                            console.error("Thumbnail load error:", e);
                            setErr("Failed to load thumbnail preview");
                        }}
                    />
                </div>
            )}

            {url && (
                <div className="space-y-3">
                    <div className="text-sm text-gray-600 dark:text-gray-300 break-all">
                        Generated URL: <code>{cleanUrl || url.split("?")[0]}</code>
                    </div>
                    <video
                        key={url}
                        src={url}
                        controls
                        loop
                        playsInline
                        muted
                        autoPlay
                        className="w-full rounded border"
                        onError={(e) => {
                            console.error("Video load error:", e);
                            setErr("Failed to load video preview. URL: " + url);
                        }}
                    />
                    <div className="flex gap-2">
                        <button
                            onClick={handleUse}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-semibold"
                        >
                            ✅ Use this background
                        </button>
                        <button
                            onClick={copyUrl}
                            className="border px-4 py-2 rounded"
                            title="Copy clean URL"
                        >
                            📋 Copy URL
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}